import { X } from 'lucide-react';
import { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { paths } from '../../routes/paths';
import { CustomerAvatar } from './PresenceDot';
import { selectCaseload } from '../../store/selectors/coachInsights';
import { getCaseloadAction } from '../../store/slices/coachInsights';
import monitoringIcon from '../../assets/insights/icons/monitoring.svg';
import { customerIdentity, DELETED_ACCOUNT_LABEL } from '../../components/UserIdentity';
import { useMediaQuery, useAppDispatch, useAppSelector, useDebouncedValue } from '../../app/hooks';

const ROW_HEIGHT = 56;
const OVERSCAN = 6;

const CustomerRow = ({ customer, selected, onSelect, index, total }) => {
  const identity = customerIdentity(customer);
  const heading = identity.heading ?? DELETED_ACCOUNT_LABEL;
  const { detail } = identity;
  return (
    <button
      type="button"
      title={identity.title || heading}
      onClick={() => onSelect(customer.id)}
      aria-current={selected ? 'true' : undefined}
      role="option"
      aria-selected={selected}
      aria-posinset={index + 1}
      aria-setsize={total}
      data-customer-index={index}
      className={cn(
        'flex h-14 w-full items-center gap-3 border-b-[0.5px] border-dq-900 px-4 text-left focus:outline-none focus-visible:bg-dq-950',
        selected ? 'bg-dq-950' : 'hover:bg-dq-950'
      )}
    >
      <CustomerAvatar initials={identity.initials} active={customer.is_active_now} />
      <span className="flex min-w-0 flex-col">
        <span
          className={cn(
            'truncate font-dq text-[14px] font-medium leading-[17px]',
            selected ? 'text-dq-title' : 'text-dq-400'
          )}
        >
          {heading}
        </span>
        {detail ? (
          <span className="truncate font-dq text-[11px] leading-[13px] text-dq-600">{detail}</span>
        ) : null}
      </span>
    </button>
  );
};

const RailBody = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const caseload = useAppSelector(selectCaseload);
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query);
  const sentinel = useRef(null);
  const scroller = useRef(null);
  const [viewport, setViewport] = useState({ top: 0, height: 900 });
  const [focusIndex, setFocusIndex] = useState(null);

  // The endpoint ignores queries under two characters, so treat them as no filter.
  const activeQuery = debounced.trim().length >= 2 ? debounced.trim() : '';
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = 0;
    setViewport((current) => ({ ...current, top: 0 }));
    setFocusIndex(null);
    dispatch(getCaseloadAction({ query: activeQuery, page: 1 }));
  }, [dispatch, activeQuery]);

  useEffect(() => {
    const element = scroller.current;
    const observer = new ResizeObserver(() =>
      setViewport((current) => ({ ...current, height: element.clientHeight }))
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (focusIndex !== null)
      scroller.current
        ?.querySelector(`[data-customer-index="${focusIndex}"]`)
        ?.focus({ preventScroll: true });
  }, [focusIndex]);

  const start = Math.max(0, Math.floor(viewport.top / ROW_HEIGHT) - OVERSCAN);
  const end = Math.min(
    caseload.items.length,
    start + Math.ceil(viewport.height / ROW_HEIGHT) + OVERSCAN * 2
  );

  const moveFocus = (event) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const index = Number(event.target.dataset.customerIndex);
    if (!Number.isFinite(index)) return;
    event.preventDefault();
    const next = Math.max(
      0,
      Math.min(
        caseload.items.length - 1,
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? caseload.items.length - 1
            : index + (event.key === 'ArrowDown' ? 1 : -1)
      )
    );
    const element = scroller.current;
    if (next * ROW_HEIGHT < element.scrollTop) element.scrollTop = next * ROW_HEIGHT;
    else if ((next + 1) * ROW_HEIGHT > element.scrollTop + element.clientHeight)
      element.scrollTop = (next + 1) * ROW_HEIGHT - element.clientHeight;
    setViewport((current) => ({ ...current, top: element.scrollTop }));
    setFocusIndex(next);
  };

  // Infinite scroll: the rail has no pager in the design.
  useEffect(() => {
    const node = sentinel.current;
    if (!node) return undefined;
    const hasMore = caseload.items.length < caseload.total;
    // A failed page must not re-arm the observer, or it would retry forever.
    if (!hasMore || caseload.loading || caseload.error) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        dispatch(getCaseloadAction({ query: caseload.query, page: caseload.page + 1 }));
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    dispatch,
    caseload.items.length,
    caseload.total,
    caseload.loading,
    caseload.query,
    caseload.page,
    caseload.error,
  ]);

  const select = (id) => {
    navigate(paths.dashboard.insights.customer(id));
    onClose?.();
  };

  return (
    <>
      <div className="mx-auto flex w-[251px] flex-col gap-4">
        <div className="flex items-center justify-between px-1">
          <span className="font-dq text-[18px] font-bold text-dq-title">Insights</span>
          <span className="flex items-center gap-2">
            <img src={monitoringIcon} alt="" className="h-4 w-4" />
            {/* Drawer only: a 44px target in the title row; negative margins keep the row's height. */}
            <button
              type="button"
              aria-label="Close customers"
              onClick={onClose}
              className="-my-3 -mr-3 flex h-11 w-11 items-center justify-center rounded-full text-dq-400 hover:bg-dq-950 lg:hidden"
            >
              <X className="h-4 w-4" />
            </button>
          </span>
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          aria-label="Search customers"
          className="h-9 w-full rounded-lg border border-dq-900 bg-dq-950 px-4 font-dq text-[12px] text-dq-title outline-none placeholder:text-dq-600 focus:border-dq-700"
        />
      </div>
      <div
        ref={scroller}
        role="listbox"
        aria-label="Customers"
        onKeyDown={moveFocus}
        onScroll={(event) => {
          const top = event.currentTarget.scrollTop;
          setViewport((current) => ({ ...current, top }));
        }}
        className="mt-3 min-h-0 flex-1 overflow-y-auto"
      >
        <div aria-hidden="true" style={{ height: start * ROW_HEIGHT }} />
        {caseload.items.slice(start, end).map((customer, offset) => (
          <CustomerRow
            key={customer.id}
            customer={customer}
            selected={customer.id === customerId}
            onSelect={select}
            index={start + offset}
            total={caseload.total}
          />
        ))}
        <div aria-hidden="true" style={{ height: (caseload.items.length - end) * ROW_HEIGHT }} />
        {!caseload.loading && caseload.items.length === 0 && (
          <p className="px-5 py-6 text-center font-dq text-[12px] font-light text-dq-disabled">
            {caseload.error ||
              (caseload.query ? 'No customers match.' : 'No customers assigned yet.')}
          </p>
        )}
        <div ref={sentinel} className="h-px" />
      </div>
    </>
  );
};

// Static 288px rail from lg up; an off-canvas drawer below, like the admin sidebar.
export const CustomerRail = ({ open, onClose }) => {
  const desktop = useMediaQuery('(min-width: 1024px)');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="Close customers"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/60 animate-fade-in lg:hidden"
        />
      )}
      <aside
        data-tour="caseload"
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-full w-[288px] shrink-0 flex-col border-r border-dq-900 bg-dq-bg pt-3.5 transition-transform duration-normal lg:static lg:z-auto lg:translate-x-0',
          // Hidden off-screen content must also leave the tab order below lg.
          open ? 'translate-x-0' : 'invisible -translate-x-full lg:visible'
        )}
        aria-hidden={open || desktop ? undefined : 'true'}
      >
        <RailBody onClose={onClose} />
      </aside>
    </>
  );
};
